import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Alert,
	IconButton,
	Collapse,
	FormControl,
	FormLabel,
	Stack,
	TextField,
	Autocomplete,
	Typography,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { Error } from "@mui/icons-material";
import React, { useEffect } from "react";
import { EditGroupInClass } from "../FirebaseQueries";
import NumberSpinner from "./NumberSpinner";
import { grey } from "@mui/material/colors";


interface IGroupData {
	id: string;
	groupNumber: number;
	assignedPlayers: string[];
}

interface Student {
	id: string;
	email: string;
	fullName: string;
}

interface Props {
	isOpen: boolean;
	onClose: () => void;
	groupId: string;
	groupNumber: number;
	allStudents: Student[]
	assignedStudents: string[];
	otherGroups: IGroupData[];
}

export default function EditClassGroupDialog(props: Props) {
	const {
		isOpen,
		onClose,
		groupId,
		groupNumber,
		allStudents,
		assignedStudents,
		otherGroups
	} = props;

	const [groupNumberEdit, setGroupNumberEdit] = React.useState(1);
	const [studentsSelected, setStudentsSelected] = React.useState<Student[]>([]);

	const groupNumError = otherGroups.some((group) => group.groupNumber === groupNumberEdit) ?
		"This group number is already in use." : "";
	const [studentsErrorLabel, setStudentsErrorLabel] = React.useState("");
	const [errorEditingGroup, setErrorEditingGroup] = React.useState(false);
	const hasValidationError: boolean = groupNumError !== "";

	useEffect(() => {
		if (isOpen) {
			setGroupNumberEdit(groupNumber);
			setStudentsSelected(allStudents.filter((student) => assignedStudents.some((assignedId) => student.id === assignedId)));
		}
	}, [isOpen])


	const handleOnEdit = async () => {
		try {
			await EditGroupInClass(groupId, groupNumberEdit, SelectedStudentIds());
			onClose();
		} catch (err) {
			setErrorEditingGroup(true);
			onClose();
			console.log(err);
		}
	}

	const handleGroupNumberUpdate = (value: any) => {
		if (value !== null) setGroupNumberEdit(value);
	}

	function studentOptions(): any[] {
		return allStudents.map((value) => ({
			id: value.id,
			label: `${value.fullName} (${value.email})`,
			email: value.email,
			fullName: value.fullName
		}));
	}

	function SelectedStudentIds(): string[] {
		return studentsSelected.map((item) => item.id);
	}

	const handleStudentSelect = (_event: any, value: any, reason: string) => {
		if (reason === "selectOption") {
			let foundInOtherGroup = false;

			for (let i = 0; i < otherGroups.length; i++) {
				if (otherGroups[i].assignedPlayers.some((id) => id === value.id)) {
					setStudentsErrorLabel("This student is already in group " + otherGroups[i].groupNumber + ".");
					foundInOtherGroup = true;
					break;
				}
			}

			if (!foundInOtherGroup) {
				if (studentsSelected.some((item) => item.id === value.id)) {
					setStudentsErrorLabel("Already selected this student.");
				} else {
					setStudentsSelected([...studentsSelected, {
						id: value.id,
						email: value.email,
						fullName: value.fullName
					}]);
					setStudentsErrorLabel("");
				}
			}
		}
		else {
			setStudentsErrorLabel("");
		}
	}

	function RemoveFromStudentsSelected(id: string) {
		setStudentsSelected(studentsSelected.filter((item) => item.id !== id))
	}

	return (
		<>
			<Dialog onClose={onClose} open={isOpen}>
				<DialogTitle>Edit Group</DialogTitle>
				<DialogContent sx={{ minWidth: 400 }}>
					<Stack spacing={1}>
						<FormControl>
							<FormLabel htmlFor="session">Group Number</FormLabel>
							<NumberSpinner value={groupNumberEdit} onValueChange={handleGroupNumberUpdate} min={1} />
							<span style={{ minHeight: 22, color: "red", marginBottom: "4px" }}>{groupNumError}</span>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="search-students">Search</FormLabel>
							<Autocomplete
								options={studentOptions()}
								sx={{ width: "100%", height: "50px" }}
								renderInput={(params) => <TextField {...params} />}
								onChange={handleStudentSelect}
							/>
							<span style={{ minHeight: 22, color: "red", marginBottom: "4px" }}>{studentsErrorLabel}</span>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="assigned-students">Assigned Students ({studentsSelected.length})</FormLabel>
							<Stack
								spacing={1}
								sx={{
									backgroundColor: "white",
									borderRadius: 1,
									minHeight: "250px",
									maxHeight: "300px",
									border: "1px solid",
									borderColor: grey[300],
									padding: "0.5em 0.5em 0.5em 1em",
									overflowY: "scroll"
								}}>
								{studentsSelected.sort((a, b) => a.fullName.localeCompare(b.fullName)).map((item) => (
									<Stack key={item.id} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
										<Typography>{item.fullName} ({item.email})</Typography>
										<IconButton onClick={() => RemoveFromStudentsSelected(item.id)}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									</Stack>
								))}
							</Stack>
						</FormControl>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button disabled={hasValidationError} onClick={handleOnEdit}>Confirm</Button>
				</DialogActions>
			</Dialog>

			<Collapse in={errorEditingGroup} mountOnEnter unmountOnExit>
				<Alert icon={<Error fontSize="inherit" />} severity="error"
					action={
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => {
								setErrorEditingGroup(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}>
					There was an error editing the group. Please try again.
				</Alert>
			</Collapse>
		</>
	)
}